import { IComment } from '../comment';
import { ITagCloudInfo } from '../commentData';
import { IUserInfo } from '../userInfo';
import { IFeedSource } from '../class';

import {
    CommentParentType,
    CommentSubjectType,
    ECommentStatus,
    ECommentSortType,
    EPlatformTravelType
} from '../../enums';

export interface ISaveSuccess {
    comment: IComment;
    showParentId: number;
}

export interface IBasePosterProps {
    parentId: number;
    parentType: CommentParentType;
    subjectType?: CommentSubjectType;
    subjectId?: number;
    showParentId?: number; // 显示结构上父评论的id,  默认值0 ,第二层楼回复谁的评论都是拍平显示
    commentParentId?: number; // 父评论的id, 默认是0
    isNeedLogin?: boolean;
    isManulAudit?: boolean; // 是否为手动审核逻辑
    showUserPosterInfo?: boolean;
    currentUserInfo?: IUserInfo;
    defaultValue?: string;
    sessionSetup?: { [key: string]: any };
    onSaveSuccess?: (data: ISaveSuccess) => void;
    onCancel?(): void;
    onFocus?(): void;
    onBlur?(): void;
}

export interface IBasePosterState {
    hasValue: boolean;
    isAnonymous: boolean;
    userNick: string;
    isSubmiting: boolean;
}

export interface ICompleteCommentState {
    isPosterShow: boolean;
}

export interface ICommentItemData extends IComment {
    showed?: boolean;
    listData?: IListData; // 待整理, 现在还是原始数据
}

export interface IListData {
    list: ICommentItemData[];
    currentPage: number;
    totalPage: number;
    size: number;
    childSize?: number;
    loading: boolean;
    totalNum: number;
    totalNumAll?: number; // 包含二级楼层的总数
}

export interface IModel {
    /** 界面展示配置 */
    setup: {
        /** 是否隐藏 默认排序设置 */
        hideSortTypeSetting?: boolean;
        /** 是否隐藏 屏蔽 */
        hideCommentBan?: boolean;
        hideCommentShare?: boolean;
        /** 屏蔽文案 ['取消屏蔽', '屏蔽'] */
        labelForBan?: string[];
        /** 是否隐藏 置顶 */
        hideCommentPin?: boolean;
        /** 是否隐藏 删除 */
        hideCommentDelete?: boolean;
        /** 是否隐藏 删除，只对讲师身份生效（用于假装屏蔽为删除的场景） */
        hideCommentDeleteForCooperator?: boolean;
        /** 是否隐藏回复按钮 */
        hideCommentReply?: boolean;
    };
    groupId?: number;
    parentId: number;
    parentType: CommentParentType;
    subjectId?: number;
    subjectType?: CommentSubjectType;
    listData: IListData;
    currentUserInfo?: IUserInfo; // 当前登录用户的用户信息
    studentId?: number; // 筛选特定的人发言
    sortType?: ECommentSortType;
    keyWord?: string;
    depth?: number;
    canReport?: boolean;
    status?: ECommentStatus; // 0:审核 1:正常 2:屏蔽
    isNeedLogin?: boolean; //  是否需要登录
    isManulAudit?: boolean; //  是否为手动审核
    isCurrentUserCooperator?: boolean; //  是否 讲师或协同者
    savedSortType?: string;
    toggleEditor?: (commentInfo: IComment) => void; // 展开自定义的评论框
    updateStatInfo?: () => void; // 更新外部的统计信息，目前在 management 里有用
    onMinusCommentNum?: () => void
}

export interface ICommentsProps {
    setup: IModel['setup'];
    groupId?: number;
    parentId: number;
    parentType: CommentParentType;
    listData?: IListData;
    isNeedLogin?: boolean;
    isManulAudit?: boolean;
    isAutoLoad?: boolean;
    showUserPosterInfo?: boolean;
    previewPageCount?: number;
    isCurrentUserCooperator?: boolean;
    currentUserInfo?: IUserInfo;
    subjectType?: CommentSubjectType;
    subjectId?: number;
    sessionSetup?: { [key: string]: any };
    onShowAllClick?(): void;
    canReport?: boolean;
}

export interface IClassFeedCommentsProps extends ICommentsProps {
    currentUserInfo: IUserInfo;
    onUpdateStatInfo(): Promise<{ [key: string]: number }>;
}

export interface IClassFeedCommentsState extends ICompleteCommentState {
    currentIndex: number;
    statInfo: { [key: string]: number };
}

export interface IManagementClassFeedController<T> extends IController<T> {
    onShare: IControllerAction<T>;
}

export type IManagementClassFeedConsumerParams = ICompleteCommentConsumerParams &
    IManagementClassFeedController<ICompleteCommentModel>;

export interface ICompleteCommentModel extends IModel {
    tagCloudInfo: ITagCloudInfo;
}

export interface IMethods {
    onLoadMoreCompletion: (feedSource: IFeedSource) => void;
    onTaskView: (feedSource: IFeedSource) => void;
    onLoadMoreLikedUser?: (feedSource: IFeedSource) => void;

    toCommentPage: () => void;
    onLeaveCommentPage: () => void;
    renderFeedSimpleComment: TRenderFeedSimpleComment;
    renderFeedCommentPage: TRenderFeedCommentPage;
}

export interface IRenderFeedSimpleCommentArgs {
    feedId: number;
    data: IFeedSource;
    onCommentSuccess: (args: { total: number }) => void;
    toCommentPage: () => void;
}

export type TRenderFeedSimpleComment = (args: IRenderFeedSimpleCommentArgs) => any;

export interface IRenderFeedCommentPageArgs {
    feedId: number;
    data: IFeedSource;
    onBack: () => void;
    onCommentSuccess: (args: { total: number }) => void;
    feedElement: any;
}

export type TRenderFeedCommentPage = (args: IRenderFeedCommentPageArgs) => any;

export interface IDepartmentInfo {
    department_id: string;
    department_name: string;
    is_del: string;
}
export interface IDepartmentList {
    department_id: string;
    department_name: string;
    parent_department_id: string;
    member_count: number;
    show_index: string;
    enterprise_id: string;
    level: string;
    have_child_departments_flag: number;
    is_del: number;
    manage_permission?: number;
    managers: IMemberList[];
    child_path: IDepartmentInfo[];
    parent_path: IDepartmentInfo[];
    children?: IDepartmentList[];
}

export interface IDepartmentTreeItem extends IDepartmentList {
    key: string;
    title: string;
    isLeaf: boolean;
}

export interface IUserInfoS extends IUserInfo {
    login_name: string;
}

export interface IMemberList {
    member_id: string;
    user_info: IUserInfoS;
    department_id: number;
    role: number;
    role_type: string;
    number: string;
    name: string;
    isSelect: boolean;
}

export interface IControllerAction<T> {
    (...args: any[]): T | Promise<T>;
    status?: 'resolved' | 'pending';
}

export interface IController<T> {
    [prop: string]: IControllerAction<T>;
}

export interface ICommentActionsController<T> extends IController<T> {
    onLikeTypeChange: IControllerAction<T>;
    onSaveSuccess: IControllerAction<T>;
    onEditSuccess: IControllerAction<T>;
    onUpdatePin: IControllerAction<T>;
    onDelete: IControllerAction<T>;
    onBanded: IControllerAction<T>;
    onAudit: IControllerAction<T>;
    onShare: IControllerAction<T>;
}

export interface ICommentListController<T> extends IController<T> {
    loadMoreStart: IControllerAction<T>;
    loadMore: IControllerAction<T>;
}

export interface IDepartment {
    parents: IDepartment;
    children?: IDepartment[];
    name: string;
}

export interface IListItem {
    type: EPlatformTravelType;
    id: string;
    isSelected: boolean;
    name: string; // 内容
    avatar?: string;
    oriData?: any;
    isHaveNextLevel?: boolean;
    checkable?: boolean;

    // cbClick(cb: IListItem): void;
    // cbRightIcon(cb: IListItem): void;
}

export interface IBaseCommentListProps {
    parentType?: CommentParentType;
    parentCommentId?: number; // 真实的父节点id
    showParentId?: number; // 显示的parentId，显示在谁下面。需求是需要把子孙节点拍平展示
    sessionSetup?: { [key: string]: any };
    showUserPosterInfo?: boolean;
    currentUserInfo?: IUserInfo;
    listData: IListData;
    emptyContent?: any;
    emptyLabel?: string;
    keyWord?: string;
    loadMore?: (
        params?: Partial<{
            showParentId: number;
            depth: number;
            page: number;
            keyWord: string;
            sortType: number;
            studentId: number;
        }>,
        replace?: boolean
    ) => void;
}

export interface IBaseCommentListState {
    isCollapse: boolean;
}

export interface IBaseCommentItemProps {
    parentType?: CommentParentType;
    parentCommentId?: number;
    showParentId?: number;
    platform?: 'pc' | 'm';
    commentData: ICommentItemData;
    sessionSetup?: { [key: string]: any };
    showUserPosterInfo?: boolean;
    currentUserInfo?: IUserInfo;
    CommentConsumer: any;
    keyWord?: string;
}

export interface IBaseCommentItemState {
    isShowReplyActionPanel: boolean;
    isShowActionMenu: boolean;
    isShowEditActionPanel: boolean;
}

export interface IImgArrItem {
    thumbnail?: string;
    imageUrl: string;
}

export interface ISubmitResultData {
    score?: string;
    message?: string;
}

export interface ISubmitHandle<T> {
    submitHandle(): Promise<T> | undefined;
}

export interface ITagController<T> extends IController<T> {
    getTagCloudList: IControllerAction<T>;
    loadCommentSortType: IControllerAction<T>;
}

// comment context start
export type IIndexController<T extends IModel = IModel> = ICommentActionsController<T> & ICommentListController<T>;
export type ICommentProviderProps<T extends IModel = IModel> = Partial<T>;
export type IConsumerParams<T extends IModel = IModel> = T & IIndexController<T>;
// comment context end

// complete comment context start
export type ICompleteCommentConsumerParams = ICompleteCommentModel &
    IIndexController<ICompleteCommentModel> &
    ITagController<ICompleteCommentModel>;
// complete comment context end

export interface IImageContent {
    index: 0;
    comment: string;
    is_selected: number;
    picture_url: string;
}

export type IImageContentList = IImageContent[];

// audio slide player start
export interface IPoint {
    x: number;
    y: number;
}

export interface ISegment {
    startTime: number;
    strokeWidth: number;
    strokeColor: string;
    duration: number;
    points: IPoint[];
    leaveTime?: number;
}
// audio slide player end
